import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Search, Filter, ChevronLeft, ChevronRight, ArrowUpDown,
  MapPin, Clock, Shield, Package, Loader2, TrendingUp, TrendingDown
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import axios from "axios";
import { toast } from "sonner";
import { productImage } from "../lib/media";
import { DEFAULT_FILTERS, buildProductParams, countActiveFilters } from "../lib/productQuery";

const SIDEBAR_MOTION = { initial: { width: 0, opacity: 0 }, animate: { width: 280, opacity: 1 }, exit: { width: 0, opacity: 0 }, transition: { duration: 0.3 } };
const CARD_MOTION = { initial: { opacity: 0, y: 15 }, animate: { opacity: 1, y: 0 } };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_OPTIONS = [
  { label: "Any", value: "" },
  { label: "Under 5 days", value: "5" },
  { label: "Under 7 days", value: "7" },
  { label: "Under 14 days", value: "14" },
];

// Mini chart component
function MiniChart({ symbol, label, color }) {
  const [points, setPoints] = useState([]);
  const [price, setPrice] = useState(0);
  const [change, setChange] = useState(0);

  useEffect(() => {
    axios.get(`${API}/prices/history?symbol=${symbol}&period=7d`).then(res => {
      const data = res.data;
      if (data.length > 0) {
        const prices = data.map(d => d.price);
        setPoints(prices);
        setPrice(prices[prices.length - 1]);
        const first = prices[0];
        const last = prices[prices.length - 1];
        setChange(Math.round((last - first) / first * 10000) / 100);
      }
    }).catch(() => { /* price history unavailable */ });
  }, [symbol]);

  const pathD = useMemo(() => {
    if (points.length < 2) return "";
    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = (max - min) || Math.max(1, max * 0.01);
    const w = 200;
    const h = 50;
    return points.map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = 8 + (h - 16) - ((p - min) / range) * (h - 16);
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    }).join(" ");
  }, [points]);

  const isUp = change >= 0;

  return (
    <div className="bento-card rounded-lg border border-ib-steel/20 bg-ib-surface/50 p-4" data-testid={`mini-chart-${symbol}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="font-jetbrains text-xs font-bold text-ib-text">{label}</span>
          <span className="font-jetbrains text-[10px] text-ib-text-muted ml-2">{symbol}</span>
        </div>
        <div className={`flex items-center gap-1 font-jetbrains text-xs ${isUp ? "text-ib-positive" : "text-ib-negative"}`}>
          {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {isUp ? "+" : ""}{change}%
        </div>
      </div>
      <div className="flex items-end justify-between">
        <span className="font-jetbrains text-lg font-bold text-ib-text">{price ? `₹${price.toLocaleString("en-IN")}` : "..."}</span>
        <svg width="120" height="40" viewBox="0 0 200 50" className="flex-shrink-0">
          {pathD && (
            <>
              <defs>
                <linearGradient id={`grad-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" style={{ stopColor: color }} stopOpacity="0.3" />
                  <stop offset="100%" style={{ stopColor: color }} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`${pathD} L 200 50 L 0 50 Z`} fill={`url(#grad-${symbol})`} />
              <path d={pathD} fill="none" style={{ stroke: color }} strokeWidth="2" strokeLinecap="round" />
            </>
          )}
        </svg>
      </div>
      <span className="font-jetbrains text-[9px] text-ib-text-muted">per ton / 7-day trend from daily rate updates</span>
    </div>
  );
}

export default function MarketplacePage() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pricesUpdatedAt, setPricesUpdatedAt] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ metal_types: [], states: [], certifications: [], grades: [] });

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const setField = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const { search, metalType, grade, state, certification, maxDelivery, priceRange } = filters;
  const setSearch = setField("search"), setMetalType = setField("metalType"), setGrade = setField("grade");
  const setState = setField("state"), setCertification = setField("certification"), setMaxDelivery = setField("maxDelivery");
  const setPriceRange = setField("priceRange");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    axios.get(`${API}/products/filters/options`).then(res => setFilterOptions(res.data)).catch(() => { /* filter options unavailable */ });
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildProductParams(filters, sortBy, sortOrder, page);
      const { data } = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(data.products);
      setTotal(data.total);
      setTotalPages(data.total_pages);
      setPricesUpdatedAt(data.prices_updated_at);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load products. Please retry.");
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortOrder, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const clearFilters = () => { setFilters(DEFAULT_FILTERS); setPage(1); };

  const activeFilterCount = countActiveFilters(filters);

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="marketplace-page">
      <div className="max-w-7xl mx-auto px-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <Link to="/" className="font-outfit text-sm text-ib-text-secondary hover:text-ib-blue transition-colors mb-2 inline-block" data-testid="marketplace-back-home">
              <ChevronLeft className="w-3 h-3 inline mr-1" />Home
            </Link>
            <h1 className="font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight text-ib-text" data-testid="marketplace-title">
              Metal <span className="text-ib-blue">Marketplace</span>
            </h1>
            <p className="font-manrope text-sm text-ib-text-secondary mt-1">
              {total} products from verified Indian suppliers · All rates in ₹/MT
              {pricesUpdatedAt && <span data-testid="prices-updated-at" className="font-jetbrains text-[11px] text-ib-lime ml-2">· Rates updated {new Date(pricesUpdatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ib-text-muted" />
              <Input data-testid="marketplace-search" placeholder="Search HR coil, TMT, grade..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10 bg-ib-surface border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-manrope text-sm focus:border-ib-blue" />
            </div>
            <Button variant="outline" data-testid="marketplace-filter-toggle" onClick={() => setFiltersOpen(!filtersOpen)} className="border-ib-steel/30 text-ib-text-secondary hover:text-ib-blue font-outfit text-sm gap-2 relative">
              <Filter className="w-4 h-4" /> Filters
              {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-ib-blue text-white text-[10px] flex items-center justify-center font-jetbrains">{activeFilterCount}</span>}
            </Button>
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
              <SelectTrigger data-testid="marketplace-sort" className="w-36 bg-ib-surface border-ib-steel/30 text-ib-text font-manrope text-sm">
                <ArrowUpDown className="w-3 h-3 mr-1" /><SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent className="bg-ib-surface border-ib-steel/30">
                <SelectItem value="name" className="text-ib-text font-manrope text-sm">Name</SelectItem>
                <SelectItem value="price" className="text-ib-text font-manrope text-sm">Price</SelectItem>
                <SelectItem value="delivery_days" className="text-ib-text font-manrope text-sm">Delivery</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")} data-testid="marketplace-sort-order" className="text-ib-text-secondary hover:text-ib-blue">
              <ArrowUpDown className={`w-4 h-4 transition-transform ${sortOrder === "desc" ? "rotate-180" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Mini Charts */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-testid="marketplace-charts">
          <MiniChart symbol="BLT" label="Billet" color="var(--chart-lime)" />
          <MiniChart symbol="HRC" label="HR Coil" color="var(--chart-blue)" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 flex gap-6">
        {/* Sidebar Filters */}
        <AnimatePresence>
          {filtersOpen && (
            <motion.aside {...SIDEBAR_MOTION} className="hidden lg:block flex-shrink-0 overflow-hidden" data-testid="marketplace-filters-sidebar">
              <div className="w-[280px] glass rounded-xl p-5 sticky top-24 space-y-5 max-h-[calc(100vh-120px)] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="font-outfit font-bold text-sm text-ib-text">Filters</h3>
                  {activeFilterCount > 0 && <button onClick={clearFilters} data-testid="marketplace-clear-filters" className="font-jetbrains text-[10px] text-ib-blue hover:text-ib-lime">Clear All</button>}
                </div>

                {/* Metal Type */}
                <div>
                  <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Metal Type</label>
                  <Select value={metalType} onValueChange={(v) => { setMetalType(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="filter-metal-type" className="bg-ib-surface border-ib-steel/30 text-ib-text font-manrope text-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
                    <SelectContent className="bg-ib-surface border-ib-steel/30">
                      <SelectItem value="all" className="text-ib-text font-manrope text-sm">All Types</SelectItem>
                      {filterOptions.metal_types?.map(t => <SelectItem key={t} value={t} className="text-ib-text font-manrope text-sm">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Grade */}
                <div>
                  <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Grade</label>
                  <Select value={grade} onValueChange={(v) => { setGrade(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="filter-grade" className="bg-ib-surface border-ib-steel/30 text-ib-text font-manrope text-sm"><SelectValue placeholder="All Grades" /></SelectTrigger>
                    <SelectContent className="bg-ib-surface border-ib-steel/30">
                      <SelectItem value="all" className="text-ib-text font-manrope text-sm">All Grades</SelectItem>
                      {filterOptions.grades?.map(g => <SelectItem key={g} value={g} className="text-ib-text font-manrope text-sm">{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* State */}
                <div>
                  <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Supplier State</label>
                  <Select value={state} onValueChange={(v) => { setState(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="filter-state" className="bg-ib-surface border-ib-steel/30 text-ib-text font-manrope text-sm"><SelectValue placeholder="All States" /></SelectTrigger>
                    <SelectContent className="bg-ib-surface border-ib-steel/30">
                      <SelectItem value="all" className="text-ib-text font-manrope text-sm">All States</SelectItem>
                      {filterOptions.states?.map(s => <SelectItem key={s} value={s} className="text-ib-text font-manrope text-sm">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Certification */}
                <div>
                  <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Certification</label>
                  <Select value={certification} onValueChange={(v) => { setCertification(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="filter-certification" className="bg-ib-surface border-ib-steel/30 text-ib-text font-manrope text-sm"><SelectValue placeholder="Any Certification" /></SelectTrigger>
                    <SelectContent className="bg-ib-surface border-ib-steel/30">
                      <SelectItem value="all" className="text-ib-text font-manrope text-sm">Any</SelectItem>
                      {filterOptions.certifications?.map(c => <SelectItem key={c} value={c} className="text-ib-text font-manrope text-sm">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Delivery */}
                <div>
                  <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Delivery Timeline</label>
                  <Select value={maxDelivery} onValueChange={(v) => { setMaxDelivery(v === "any" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="filter-delivery" className="bg-ib-surface border-ib-steel/30 text-ib-text font-manrope text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent className="bg-ib-surface border-ib-steel/30">
                      {DELIVERY_OPTIONS.map(d => <SelectItem key={d.value || "any"} value={d.value || "any"} className="text-ib-text font-manrope text-sm">{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Price Range (INR/t)</label>
                  <Slider data-testid="filter-price-range" min={0} max={300000} step={5000} value={priceRange} onValueChange={(v) => { setPriceRange(v); setPage(1); }} className="mt-3" />
                  <div className="flex justify-between mt-2">
                    <span className="font-jetbrains text-[10px] text-ib-text-muted">₹{priceRange[0].toLocaleString("en-IN")}</span>
                    <span className="font-jetbrains text-[10px] text-ib-text-muted">₹{priceRange[1].toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Product Grid */}
        <div className="flex-1 min-w-0">
          <ProductGridContent
            loading={loading}
            products={products}
            clearFilters={clearFilters}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
          />
        </div>
      </div>
    </div>
  );
}

function ProductGridContent({ loading, products, clearFilters, totalPages, page, setPage }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="marketplace-loading">
        <Loader2 className="w-6 h-6 text-ib-blue animate-spin" />
        <span className="ml-3 font-manrope text-sm text-ib-text-secondary">Loading products...</span>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-20" data-testid="marketplace-empty">
        <Package className="w-12 h-12 text-ib-text-muted mx-auto mb-4" />
        <h3 className="font-outfit font-bold text-lg text-ib-text mb-2">No products found</h3>
        <p className="font-manrope text-sm text-ib-text-secondary mb-4">Try adjusting your filters</p>
        <Button onClick={clearFilters} variant="outline" className="border-ib-steel/30 text-ib-text-secondary hover:text-ib-blue font-outfit text-sm">Clear All Filters</Button>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="marketplace-grid">
        {products.map((product, i) => (
          <ProductCard key={product.id} product={product} index={i} />
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8" data-testid="marketplace-pagination">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="border-ib-steel/30 text-ib-text-secondary" data-testid="pagination-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-jetbrains text-sm text-ib-text-secondary">Page {page} of {totalPages}</span>
          <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="border-ib-steel/30 text-ib-text-secondary" data-testid="pagination-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
    </>
  );
}

function ProductCard({ product, index }) {
  return (
    <Link to={`/marketplace/${product.id}`} data-testid={`product-card-${index}`}>
      <motion.div
        {...CARD_MOTION}
        transition={{ duration: 0.3, delay: index * 0.04 }}
        className="bento-card rounded-lg border border-ib-steel/20 bg-ib-surface/50 flex flex-col h-full overflow-hidden"
      >
        <div className="h-32 overflow-hidden relative">
          <img src={productImage(product.category)} alt={product.category} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-testid={`product-image-${index}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-ib-surface/90 to-transparent" />
          <span className="absolute bottom-2 left-3 font-jetbrains text-[9px] text-ib-lime tracking-wide">{product.category}</span>
        </div>
        <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-outfit font-bold text-sm text-ib-text truncate" title={product.name}>{product.name}</h3>
            <p className="font-jetbrains text-[10px] text-ib-text-muted mt-0.5">{product.supplier}</p>
          </div>
          <Badge variant="outline" className="border-ib-steel/30 text-ib-lime font-jetbrains text-[9px] shrink-0 ml-2">{product.metal_type}</Badge>
        </div>
        <p className="font-manrope text-xs text-ib-text-secondary leading-relaxed mb-3 line-clamp-2">{product.description}</p>
        <div className="grid grid-cols-2 gap-1.5 mb-3 text-[10px]">
          <div className="flex items-center gap-1 text-ib-text-muted font-jetbrains"><MapPin className="w-3 h-3 text-ib-blue" />{product.state || product.location?.split(",")[0]}</div>
          <div className="flex items-center gap-1 text-ib-text-muted font-jetbrains"><Clock className="w-3 h-3 text-ib-blue" />{product.delivery_days}d</div>
          <div className="flex items-center gap-1 text-ib-text-muted font-jetbrains"><Package className="w-3 h-3 text-ib-blue" />{product.quantity_available?.toLocaleString()}t</div>
          <div className="flex items-center gap-1 text-ib-text-muted font-jetbrains"><Shield className="w-3 h-3 text-ib-blue" />{product.grade}</div>
        </div>
        <div className="flex flex-wrap gap-1 mb-3">
          {product.certifications?.slice(0, 2).map(c => (
            <span key={c} className="px-1.5 py-0.5 rounded text-[9px] font-jetbrains bg-ib-steel/10 text-ib-text-muted border border-ib-steel/10">{c}</span>
          ))}
        </div>
        <div className="mt-auto pt-3 border-t border-ib-steel/10 flex items-end justify-between">
          <div>
            <span className="font-jetbrains text-base font-bold text-ib-lime">₹{Number(product.price).toLocaleString("en-IN")}</span>
            <span className="font-jetbrains text-[10px] text-ib-text-muted ml-1">{product.currency}</span>
          </div>
          <span className="font-outfit text-[10px] text-ib-blue hover:underline">View Details</span>
        </div>
        </div>
      </motion.div>
    </Link>
  );
}
